import type {
  PermitRiskAssessmentPayload,
  WorkAreaPayload,
} from "modules/permit"
import type {
  WorkAreaSelection,
  WorkAreaMarker,
  WorkAreaRectangle,
  GpsAreaBounds,
} from "modules/permit/components/WorkAreaSection"

// ---- Risk helpers ---------------------------------------------------------

export type RiskState = {
  consequence: {
    personnel: string
    equipment: string
    environment: string
    property: string
  }
  likelihood: {
    conditions: string
    accident: string
  }
}

export const createEmptyRiskState = (): RiskState => ({
  consequence: {
    personnel: "",
    equipment: "",
    environment: "",
    property: "",
  },
  likelihood: {
    conditions: "",
    accident: "",
  },
})

export const parseRiskScore = (value: string): number => {
  const numeric = Number.parseInt(value, 10)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0
}

export const determineRiskLevel = (score: number): string => {
  if (score <= 0) return "Not Assessed"
  if (score === 1) return "Low Risk"
  if (score >= 2 && score <= 4) return "Acceptable Risk"
  if (score >= 6 && score <= 12) return "High Risk"
  if (score >= 16) return "Extreme Risk"
  return "High Risk"
}

export const buildRiskAssessmentPayload = (state: RiskState): PermitRiskAssessmentPayload | null => {
  const personScore = parseRiskScore(state.consequence.personnel)
  const communityScore = parseRiskScore(state.consequence.equipment)
  const environmentScore = parseRiskScore(state.consequence.environment)
  const assetScore = parseRiskScore(state.consequence.property)
  const quantitativeScore = parseRiskScore(state.likelihood.conditions)
  const qualitativeScore = parseRiskScore(state.likelihood.accident)
  const allScores = [
    personScore,
    communityScore,
    environmentScore,
    assetScore,
    quantitativeScore,
    qualitativeScore,
  ]
  const consequenceScore = Math.max(personScore, communityScore, environmentScore, assetScore, 0)
  const likelihoodScore = Math.max(quantitativeScore, qualitativeScore, 0)

  if (allScores.some((score) => score <= 0)) {
    return null
  }

  const riskScore = consequenceScore * likelihoodScore
  const riskLevel = determineRiskLevel(riskScore)

  return {
    personScore,
    communityScore,
    environmentScore,
    assetScore,
    quantitativeScore,
    qualitativeScore,
    consequenceScore,
    likelihoodScore,
    riskScore,
    riskLevel,
  }
}

// ---- Work area helpers ----------------------------------------------------

export const parseNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value === "number") return Number.isNaN(value) ? null : value
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (trimmed.length === 0) return null
    const parsed = Number(trimmed)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

export const toStringOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export const normaliseMarkerValue = (value: unknown): WorkAreaMarker | null => {
  if (!value || typeof value !== "object") {
    return null
  }

  const raw = value as Record<string, unknown>
  const normalisePercent = (input: unknown) => {
    const numeric = parseNullableNumber(input)
    if (numeric === null) {
      return null
    }
    return Math.max(0, Math.min(100, Number.parseFloat(numeric.toFixed(2))))
  }

  const x = normalisePercent(raw["xPercent"] ?? raw["x"])
  const y = normalisePercent(raw["yPercent"] ?? raw["y"])

  if (x === null || y === null) {
    return null
  }

  return {
    xPercent: x,
    yPercent: y,
  }
}

export const normaliseAreaValue = (value: unknown): WorkAreaRectangle | null => {
  if (!value || typeof value !== "object") {
    return null
  }

  const raw = value as Record<string, unknown>
  const clamp = (input: unknown) => {
    const numeric = parseNullableNumber(input)
    if (numeric === null) {
      return null
    }
    return Number.parseFloat(Math.max(0, Math.min(100, numeric)).toFixed(2))
  }

  const x = clamp(raw["xPercent"] ?? raw["x"] ?? raw["left"])
  const y = clamp(raw["yPercent"] ?? raw["y"] ?? raw["top"])
  let width = clamp(raw["widthPercent"] ?? raw["width"])
  let height = clamp(raw["heightPercent"] ?? raw["height"])
  const x2 = clamp(raw["x2Percent"] ?? raw["rightPercent"] ?? raw["right"])
  const y2 = clamp(raw["y2Percent"] ?? raw["bottomPercent"] ?? raw["bottom"])

  if (width === null && x !== null && x2 !== null) {
    width = Number.parseFloat(Math.max(0, x2 - x).toFixed(2))
  }
  if (height === null && y !== null && y2 !== null) {
    height = Number.parseFloat(Math.max(0, y2 - y).toFixed(2))
  }

  if (x === null || y === null || width === null || height === null) {
    return null
  }
  if (width <= 0 || height <= 0) {
    return null
  }

  return {
    xPercent: x,
    yPercent: y,
    widthPercent: width,
    heightPercent: height,
  }
}

export const normaliseAreaList = (value: unknown): WorkAreaRectangle[] => {
  if (value === null || value === undefined) {
    return []
  }

  let raw: unknown = value
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (!trimmed) {
      return []
    }
    try {
      raw = JSON.parse(trimmed)
    } catch {
      return []
    }
  }

  if (Array.isArray(raw)) {
    return raw
      .map((item) => normaliseAreaValue(item))
      .filter((item): item is WorkAreaRectangle => item !== null)
  }

  const single = normaliseAreaValue(raw)
  return single ? [single] : []
}

export const normaliseGpsAreaValue = (value: unknown): GpsAreaBounds | null => {
  if (!value || typeof value !== "object") {
    return null
  }

  const raw = value as Record<string, unknown>
  const north = parseNullableNumber(raw["north"])
  const south = parseNullableNumber(raw["south"])
  const east = parseNullableNumber(raw["east"])
  const west = parseNullableNumber(raw["west"])

  if (north === null || south === null || east === null || west === null) {
    return null
  }

  return {
    north: Number.parseFloat(north.toFixed(6)),
    south: Number.parseFloat(south.toFixed(6)),
    east: Number.parseFloat(east.toFixed(6)),
    west: Number.parseFloat(west.toFixed(6)),
  }
}

export const normaliseGpsAreaList = (value: unknown): GpsAreaBounds[] => {
  if (value === null || value === undefined) {
    return []
  }

  let raw: unknown = value
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (!trimmed) {
      return []
    }
    try {
      raw = JSON.parse(trimmed)
    } catch {
      return []
    }
  }

  if (Array.isArray(raw)) {
    return raw
      .map((item) => normaliseGpsAreaValue(item))
      .filter((item): item is GpsAreaBounds => item !== null)
  }

  const single = normaliseGpsAreaValue(raw)
  return single ? [single] : []
}

export const buildWorkAreaPayload = (
  selection: WorkAreaSelection | null | undefined,
): WorkAreaPayload | null => {
  if (!selection) {
    return null
  }

  const mapMode = selection.mapMode === "gps" ? "gps" : "plot-plan"
  const areas =
    selection.areas && selection.areas.length > 0
      ? selection.areas
      : selection.area
        ? [selection.area]
        : []
  const lastArea = areas.length > 0 ? areas[areas.length - 1] : null
  const payload: WorkAreaPayload = {
    mapMode,
    plotPlanId: selection.plotPlanId ?? null,
    plotPlanVersionId: selection.plotPlanVersionId ?? null,
    plotPlanName: selection.plotPlanName ?? null,
    plotPlanImagePath: selection.plotPlanImagePath ?? null,
    marker: mapMode === "gps" ? null : selection.marker ?? null,
    area: lastArea,
    areas: areas.length > 0 ? areas : null,
    gpsLatitude: selection.gpsLatitude ?? null,
    gpsLongtitude: selection.gpsLongtitude ?? null,
    gpsAreas: selection.gpsAreas ?? null,
  }

  const hasMarker = payload.marker != null
  const hasArea = payload.area != null
  const hasGps =
    payload.gpsLatitude !== null ||
    payload.gpsLongtitude !== null ||
    mapMode === "gps"
  const hasReference =
    (payload.plotPlanId && payload.plotPlanId.trim().length > 0) ||
    (payload.plotPlanVersionId && payload.plotPlanVersionId.trim().length > 0)

  if (!hasMarker && !hasArea && !hasGps && !hasReference) {
    return null
  }

  return payload
}

// ---- Legacy helper stubs (kept for compatibility) ------------------------

// Get required documents based on selected activities and permit type
export const getRequiredDocuments = (selectedActivities: string[], permitData: any) => {
  const docs = []
  
  if (selectedActivities.includes('WELD')) {
    docs.push(
      { id: 'wps', name: 'Welding Procedure Specification', code: 'WPS', isUploaded: true, source: 'activity' },
      { id: 'gas', name: 'Gas Test Report', code: 'GAS', isUploaded: true, source: 'activity' },
      { id: 'ms', name: 'Method Statement', code: 'MS', isUploaded: true, source: 'activity' }
    )
  }
  
  if (selectedActivities.includes('CRANE')) {
    docs.push(
      { id: 'crane-cert', name: 'Crane Certificate', code: 'CRANE', isUploaded: false, source: 'activity' }
    )
  }

  // Add Confine Work Space documents (support both legacy state and dynamic extra values)
  const inertGasSelection =
    permitData?.inertGas ??
    permitData?.extraFieldValues?.INERTGAS ??
    permitData?.extraFieldValues?.inertgas ??
    permitData?.extraFieldValues?.InertGas

  if (permitData.permitType === 'Confine Work Space' && inertGasSelection === 'INERT GAS CONFINED SPACE') {
    docs.push({
      id: 'inert-gas-safety',
      name: 'แบบตรวจสอบความปลอดภัยก่อนเข้าทำงานในที่อับอากาศภายใต้บรรยากาศเฉื่อย (9900F-847)',
      code: '9900F-847',
      isUploaded: false,
      source: 'confine-work'
    })
  }

  if (selectedActivities.length > 0) {
    docs.push({ id: 'ppe', name: 'รายการ PPE', code: 'PPE', isUploaded: true, source: 'activity' })
  }

  return docs
}

// Get extra fields based on selected activities
export const getExtraFields = (selectedActivities: string[], extraFieldValues: any) => {
  const fields = []
  
  if (selectedActivities.includes('CRANE')) {
    fields.push(
      { id: 'crane-license', label: 'ทะเบียนรถ', value: extraFieldValues['crane-license'] || '', required: true },
      { id: 'crane-size', label: 'ขนาดเครน (ตัน)', value: extraFieldValues['crane-size'] || '', required: false }
    )
  }

  return fields
}

const padDatePart = (value: number) => String(value).padStart(2, '0')

export const formatLocalDateString = (date: Date): string =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`

export const parseLocalDateString = (dateText: string): Date | null => {
  if (typeof dateText !== 'string') return null
  const trimmed = dateText.trim()
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (!match) return null

  const year = Number.parseInt(match[1], 10)
  const month = Number.parseInt(match[2], 10)
  const day = Number.parseInt(match[3], 10)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null
  }

  const parsed = new Date(year, month - 1, day)
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null
  }

  return parsed
}

// Date validation utilities
export const validateWorkDate = (
  date: string,
  options?: { allowPastDates?: boolean; allowFutureDates?: boolean },
): { isValid: boolean; error?: string } => {
  if (!date) {
    return { isValid: true } // Don't show error for empty dates, let form validation handle required fields
  }

  const selectedDate = parseLocalDateString(date)
  const today = new Date()
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  
  // Calculate 2 years from today
  const twoYearsFromNow = new Date(todayStart)
  twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2)

  // Check if date is invalid
  if (!selectedDate) {
    return { 
      isValid: false, 
      error: 'รูปแบบวันที่ไม่ถูกต้อง' 
    }
  }

  if (!options?.allowPastDates) {
    // Check if date is in the past (before today)
    if (selectedDate < todayStart) {
      return { 
        isValid: false, 
        error: 'ไม่สามารถเลือกวันที่ย้อนหลังได้' 
      }
    }
  }

  // Check if date is more than 2 years in the future
  if (!options?.allowFutureDates && selectedDate > twoYearsFromNow) {
    return {
      isValid: false,
      error: 'ไม่สามารถเลือกวันที่ล่วงหน้าเกิน 2 ปีได้'
    }
  }

  return { isValid: true }
}

// Get min and max date strings for date input
export const getDateConstraints = (options?: { allowPastDates?: boolean; allowFutureDates?: boolean }) => {
  const today = new Date()
  const twoYearsFromNow = new Date()
  twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2)

  return {
    min: options?.allowPastDates ? null : formatLocalDateString(today),
    max: options?.allowFutureDates ? null : formatLocalDateString(twoYearsFromNow)
  }
}

// Validate date range (start date should not be after end date)
export const validateDateRange = (startDate: string, endDate: string): { isValid: boolean; error?: string } => {
  if (!startDate || !endDate) {
    return { isValid: true } // Individual dates will be validated separately
  }

  const start = parseLocalDateString(startDate)
  const end = parseLocalDateString(endDate)

  if (!start || !end) {
    return {
      isValid: false,
      error: 'รูปแบบวันที่ไม่ถูกต้อง'
    }
  }

  if (start > end) {
    return {
      isValid: false,
      error: 'วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด'
    }
  }

  return { isValid: true }
}

export const validateWorkScheduleRange = (
  startDate: string,
  endDate: string,
  startTime: string,
  endTime: string,
  options?: { allowPastDates?: boolean },
): { isValid: boolean; error?: string } => {
  const startDateValidation = validateWorkDate(startDate, options)
  if (!startDateValidation.isValid) {
    return startDateValidation
  }

  const endDateValidation = validateWorkDate(endDate, options)
  if (!endDateValidation.isValid) {
    return endDateValidation
  }

  const dateRangeValidation = validateDateRange(startDate, endDate)
  if (!dateRangeValidation.isValid) {
    return dateRangeValidation
  }

  if (!startTime || !endTime) {
    return {
      isValid: false,
      error: 'กรุณาระบุเวลาเริ่มต้นและเวลาสิ้นสุดให้ถูกต้อง',
    }
  }

  const start = parseLocalDateString(startDate)
  const end = parseLocalDateString(endDate)
  if (!start || !end) {
    return {
      isValid: false,
      error: 'รูปแบบวันที่ไม่ถูกต้อง',
    }
  }

  const timePattern = /^(\d{2}):(\d{2})$/
  const startMatch = timePattern.exec(startTime.trim())
  const endMatch = timePattern.exec(endTime.trim())

  if (!startMatch || !endMatch) {
    return {
      isValid: false,
      error: 'รูปแบบเวลาไม่ถูกต้อง',
    }
  }

  const startDateTime = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    Number.parseInt(startMatch[1], 10),
    Number.parseInt(startMatch[2], 10),
    0,
    0,
  )
  const endDateTime = new Date(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
    Number.parseInt(endMatch[1], 10),
    Number.parseInt(endMatch[2], 10),
    0,
    0,
  )

  if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
    return {
      isValid: false,
      error: 'รูปแบบเวลาไม่ถูกต้อง',
    }
  }

  if (startDateTime.getTime() >= endDateTime.getTime()) {
    return {
      isValid: false,
      error: 'เวลาสิ้นสุดต้องมากกว่าเวลาเริ่มต้น',
    }
  }

  return { isValid: true }
}

export const validatePermitExtensionRange = (
  currentEndDate: string,
  currentEndTime: string,
  newEndDate: string,
  newEndTime: string,
  startDate: string,
  startTime: string,
  maxHours = 24,
): { isValid: boolean; error?: string } => {
  const timePattern = /^(\d{2}):(\d{2})$/
  const currentDate = parseLocalDateString(currentEndDate)
  const nextDate = parseLocalDateString(newEndDate)
  const refStartDate = parseLocalDateString(startDate)
  const currentTimeMatch = timePattern.exec(currentEndTime.trim())
  const nextTimeMatch = timePattern.exec(newEndTime.trim())
  const startTimeMatch = timePattern.exec(startTime.trim())

  if (!currentDate || !nextDate || !currentTimeMatch || !nextTimeMatch) {
    return {
      isValid: false,
      error: "\u0e23\u0e39\u0e1b\u0e41\u0e1a\u0e1a\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2b\u0e23\u0e37\u0e2d\u0e40\u0e27\u0e25\u0e32\u0e44\u0e21\u0e48\u0e16\u0e39\u0e01\u0e15\u0e49\u0e2d\u0e07",
    }
  }

  const currentEnd = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate(),
    Number.parseInt(currentTimeMatch[1], 10),
    Number.parseInt(currentTimeMatch[2], 10),
    0,
    0,
  )
  const nextEnd = new Date(
    nextDate.getFullYear(),
    nextDate.getMonth(),
    nextDate.getDate(),
    Number.parseInt(nextTimeMatch[1], 10),
    Number.parseInt(nextTimeMatch[2], 10),
    0,
    0,
  )

  if (Number.isNaN(currentEnd.getTime()) || Number.isNaN(nextEnd.getTime())) {
    return {
      isValid: false,
      error: "\u0e23\u0e39\u0e1b\u0e41\u0e1a\u0e1a\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2b\u0e23\u0e37\u0e2d\u0e40\u0e27\u0e25\u0e32\u0e44\u0e21\u0e48\u0e16\u0e39\u0e01\u0e15\u0e49\u0e2d\u0e07",
    }
  }

  if (nextEnd.getTime() <= currentEnd.getTime()) {
    return {
      isValid: false,
      error: "\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48\u0e15\u0e49\u0e2d\u0e07\u0e21\u0e32\u0e01\u0e01\u0e27\u0e48\u0e32\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e40\u0e14\u0e34\u0e21",
    }
  }

  if (refStartDate && startTimeMatch) {
    const startDateTime = new Date(
      refStartDate.getFullYear(),
      refStartDate.getMonth(),
      refStartDate.getDate(),
      Number.parseInt(startTimeMatch[1], 10),
      Number.parseInt(startTimeMatch[2], 10),
      0,
      0,
    )
    const maxExtensionMs = maxHours * 60 * 60 * 1000
    if (!Number.isNaN(startDateTime.getTime()) && nextEnd.getTime() - startDateTime.getTime() > maxExtensionMs) {
      return {
        isValid: false,
        error:
          "\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e15\u0e48\u0e2d Permit \u0e44\u0e14\u0e49\u0e44\u0e21\u0e48\u0e40\u0e01\u0e34\u0e19 24 \u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07\u0e08\u0e32\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19",
      }
    }
  }

  return { isValid: true }
}
